import {trimValue} from '../utilities/utilities';
import WindowMetrics from '../utilities/window-metrics';

import ViewportScaledElement from './viewport-scaled-element';
import HotSpot from './hotspot';

const settings = {
    cssProperties: {
        backgroundAspectRatio: '--statue-background-aspect-ratio',
        statueScale: '--statues-screen-scale',
    },

    viewport: {
        // Used for scaling the statues down when the viewport isn't ideal -
        // these values are also seen in home.scss & statue-section.scss
        idealAspectRatio: {
            mobile: 930 / 750,
            desktop: 1730 / 2880,
        },
    },
};

/** Base class for the statue sections seen on several pages of the site */
export default class StatueSection extends ViewportScaledElement {
    /**
     * @constructor
     * @param {HTMLElement} container
     * @param {object} viewportSettingsOverrides
     */
    constructor(container, viewportSettingsOverrides = {}) {
        // Call base class
        super(container);

        // Merge our default viewport settings with any provided overrides
        this.viewport = {
            idealAspectRatio: Object.assign(
                {},
                settings.viewport.idealAspectRatio,
                viewportSettingsOverrides.idealAspectRatio,
            ),
        };

        // Adjustments made when the viewport isn't ideal
        this.adjustedAspectRatio = null;
        this.adjustedStatueScale = null;

        // Tracks the currently active hotspot
        this.currentHotspot = null;

        // Find any hotspots in the section
        const hotspotElements = container
            .getElementsByClassName(HotSpot.classNames.hotspot);

        const hotspotCallbacks = {
            onSelect: this.hotspotSelected.bind(this),
            onDeselect: this.hotspotDeselected.bind(this),
        };

        this.hotspots = [];
        for (let i=0; i<hotspotElements.length; i++) {
            const hotspot = new HotSpot(hotspotElements[i], hotspotCallbacks);
            this.hotspots.push(hotspot);
        };

        // Set up event to close hotspots on negative space clicks
        document.body.addEventListener('click', (event) => {
            // Stop if there's no active hotspot
            if (!this.currentHotspot) {
                return;
            }

            this.handleBodyClickEvent(event);
        });
    }

    /**
     * Signals that the viewport dimensions have changed
     * @param {number} width
     * @param {number} height
     */
    setViewportDimensions(width, height) {
        // Call base class
        super.setViewportDimensions(width, height);

        // Check if the aspect ratio is shorter, vertically, than
        // our ideal
        const newAspectRatio = height / width;
        let idealAspectRatio;
        if (WindowMetrics.isMobile()) {
            idealAspectRatio = this.viewport.idealAspectRatio.mobile;
        } else {
            idealAspectRatio = this.viewport.idealAspectRatio.desktop;
        }

        if (newAspectRatio < idealAspectRatio) {
            // Aspect ratio is too short
            this.adjustedAspectRatio = trimValue(newAspectRatio);

            this.container.style.setProperty(
                settings.cssProperties.backgroundAspectRatio,
                `${this.adjustedAspectRatio}`,
            );

            this.adjustedStatueScale = trimValue(
                newAspectRatio / idealAspectRatio,
            );

            this.container.style.setProperty(
                settings.cssProperties.statueScale,
                `${this.adjustedStatueScale}`,
            );
        } else {
            // Aspect ratio isn't too short
            if (this.adjustedAspectRatio !== null) {
                // Remove the adjusted aspect ratio we applied previously
                this.adjustedAspectRatio = null;
                this.container.style.removeProperty(
                    settings.cssProperties.backgroundAspectRatio,
                );
            }

            if (this.adjustedStatueScale !== null) {
                // Remove the adjusted statue scaling we applied previously
                this.adjustedStatueScale = null;
                this.container.style.removeProperty(
                    settings.cssProperties.statueScale,
                );
            }
        }
    }

    /**
     * Handles body click events to deselect hotspots - override in
     * derived classes to add additional logic, like stopping if an
     * element associated with a hotbox was clicked
     * @param {event} event
     */
    handleBodyClickEvent(event) {
        // Deselect hotspots
        this.deselectHotspots();
    }

    /**
     * Called when a hotspot is selected - for overriding in derived
     * classes
     * @param {HotSpot} hotspot
     * @param {boolean} keyboardEvent
     */
    hotspotSelected(
        hotspot,
        keyboardEvent,
    ) {
        this.deselectHotspots();
        this.currentHotspot = hotspot;
    }

    /**
     * Called when a hotspot is deselected - for overriding in derived
     * classes
     * @param {HotSpot} hotspot
     * @param {boolean} keyboardEvent
     */
    hotspotDeselected(
        hotspot,
        keyboardEvent,
    ) {
        if (this.currentHotspot == hotspot) {
            this.currentHotspot = null;
        }
    }

    /**
     * Deselects any hotspots (currently only one may be selected
     * at a time)
     */
    deselectHotspots() {
        if (this.currentHotspot) {
            this.currentHotspot.deselect();
        }
    }
}
